/**
 * Локаль интерфейса для форматирования вне React-дерева.
 *
 * В компонентах локаль берётся из next-intl (`useFormatter`, `useLocale`).
 * Этот модуль нужен там, где хука нет: чистые функции форматирования дат
 * (`msk-time`), сообщения об ошибках, утилиты. На сервере отдаёт `ru` —
 * серверный рендер локаль знает из cookie и передаёт её вниз явно.
 *
 * KAN-997: заводится, чтобы убрать захардкоженный "ru-RU" из кода —
 * при английском интерфейсе дата оставалась русской.
 */

export type UiLocale = "ru" | "en";

/** Локаль интерфейса: из атрибута lang документа, иначе из языка браузера. */
export function getUiLocale(): UiLocale {
  if (typeof window === "undefined") return "ru";
  const lang = document.documentElement.lang || navigator.language || "";
  return lang.startsWith("en") ? "en" : "ru";
}

/** BCP-47 тег для Intl: локаль интерфейса, а не язык браузера. */
export function uiLocaleTag(locale: UiLocale = getUiLocale()): string {
  return locale === "en" ? "en-US" : "ru-RU";
}

/** Локаль next-intl (`useLocale()`) → UiLocale. */
export function toUiLocale(locale: string): UiLocale {
  return locale.startsWith("en") ? "en" : "ru";
}

/**
 * Число по локали интерфейса: «1 000» на русском, «1,000» на английском.
 *
 * В компоненте передавай `toUiLocale(useLocale())`: тогда серверный рендер и
 * гидратация дадут одну строку. Без `locale` берётся `getUiLocale()` — годится
 * для того, что рисуется только на клиенте (KAN-1374).
 */
export function formatNumberUi(
  value: number,
  options?: Intl.NumberFormatOptions,
  locale?: UiLocale,
): string {
  return new Intl.NumberFormat(uiLocaleTag(locale), options).format(value);
}
